import { images } from '../images';
import type { Course } from '../../types/content';

export const youngLearnerCourses: Course[] = [
  {
    slug: 'language-camps',
    name: 'Language Camps',
    group: 'young',
    season: 'seasonal',
    keyword: 'english summer camp',
    metaTitle: 'English Summer Camp for Children and Teens | ES World',
    metaDescription: 'Our English summer camp mixes lessons, activities and trips. Safe care for ages 8 to 17. See camp dates and prices.',
    h1: 'English summer camp with lessons and activities',
    lead: 'Our English summer camp gives young learners English lessons every morning and activities every afternoon. Students aged 8 to 17 make friends from many countries. Group leaders look after them all day, and parents receive regular updates.',
    cardText: 'Morning lessons, afternoon activities and weekend trips.',
    audience: 'Ages 8 to 17',
    image: images.young,
    facts: [
      { label: 'Camp length', value: '1 to 6 weeks' },
      { label: 'Lessons', value: '15 a week' },
      { label: 'Ages', value: '8 to 17' },
      { label: 'Dates', value: 'Summer and winter' },
    ],
    valueTitle: 'Learn English and make friends',
    valueText: 'Your child practises English in class and then uses it in games, sports and trips on the same day. Every day is planned and supervised.',
    outcomes: [
      'Speak English more often and with less fear',
      'Make friends with young people from other countries',
      'Try new sports, arts and team games',
      'Visit places of interest with group leaders',
      'Become more independent in a safe place',
      'Receive a certificate at the end of camp',
    ],
    whoTitle: 'Safe camps for young learners',
    whoText: 'Camps suit children and teens who want to improve their English in the school holidays. Students join groups by age and level.',
    whoFor: [
      { title: 'Children aged 8 to 12', text: 'Short, active lessons with lots of games.' },
      { title: 'Teens aged 13 to 17', text: 'More speaking, projects and city trips.' },
      { title: 'First-time travellers', text: 'Your child has never studied abroad before.' },
      { title: 'Parents', text: 'You want a clear daily plan and regular updates.' },
    ],
    weekTitle: 'A sample camp week',
    weekText: 'Mornings are for English lessons. Afternoons and evenings are for activities. Saturday is a full-day trip with group leaders.',
    week: [
      { day: 'Monday', focus: 'Welcome, level test and team games' },
      { day: 'Tuesday', focus: 'Lessons, then a sports afternoon' },
      { day: 'Wednesday', focus: 'Lessons, then arts and drama' },
      { day: 'Thursday', focus: 'Lessons, then a local half-day trip' },
      { day: 'Friday', focus: 'Lessons, project show and evening party' },
    ],
    faqs: [
      { q: 'Who looks after my child?', a: 'Trained group leaders stay with students during activities, trips and free time. A welfare team is available day and night.' },
      { q: 'Is airport transfer included?', a: 'Airport transfer is available on set arrival days. Our team will confirm the details for your camp.' },
    ],
    ctas: {
      heroPrimary: 'Ask about camp dates',
      heroSecondary: 'See a camp week',
      midPrimary: 'See camp prices',
      midLink: { label: 'Read our guide for parents', to: '/blog/english-summer-camp-guide-for-parents' },
      bottomPrimary: 'Send my camp enquiry',
    },
    locations: ['dubai', 'london', 'oxfordshire', 'new-york', 'new-haven', 'los-angeles', 'tokyo', 'seoul', 'hong-kong', 'cape-town'],
  },
  {
    slug: 'academic-camps',
    name: 'Academic Camps',
    group: 'young',
    season: 'seasonal',
    keyword: 'academic summer camp',
    metaTitle: 'Academic Summer Camp for Teens | English and Subjects | ES World',
    metaDescription: 'Our academic summer camp mixes English with science, business and design classes. For teens aged 13 to 17. See dates.',
    h1: 'Academic summer camp for curious teens',
    lead: 'Our academic summer camp mixes English lessons with subject classes for teens. Students explore topics such as science, business or design in English. They also practise research, teamwork and presentation skills on a campus with modern classrooms.',
    cardText: 'English lessons plus subject classes and team projects.',
    audience: 'Ages 13 to 17',
    image: images.young,
    facts: [
      { label: 'Camp length', value: '2 to 4 weeks' },
      { label: 'Lessons', value: '000 a week' },
      { label: 'Ages', value: '13 to 17' },
      { label: 'Dates', value: 'Summer' },
    ],
    valueTitle: 'Explore new subjects in English',
    valueText: 'Students learn subject words and ideas in English, and then use them in team projects. This helps them prepare for study abroad in the future.',
    outcomes: [
      'Use English to talk about science, business or design',
      'Work in a team on a real project',
      'Present ideas to a group with confidence',
      'Practise research and note-taking skills',
      'Experience daily life on a campus',
      'Receive a certificate and a teacher report',
    ],
    whoTitle: 'For teens who plan ahead',
    whoText: 'Academic Camps suit teens with an intermediate level who are thinking about study abroad in the future.',
    whoFor: [
      { title: 'Future university students', text: 'You want to see what study abroad is like.' },
      { title: 'Curious learners', text: 'You enjoy science, business or creative subjects.' },
      { title: 'Confident speakers', text: 'You want more challenge than a language camp.' },
      { title: 'Parents', text: 'You want your child to build study skills in English.' },
    ],
    weekTitle: 'A week of learning by doing',
    weekText: 'Each day mixes English lessons with subject workshops. On Friday, teams present their projects to the whole group.',
    week: [
      { day: 'Monday', focus: 'Welcome, team building and project briefing' },
      { day: 'Tuesday', focus: 'English lessons and a subject workshop' },
      { day: 'Wednesday', focus: 'Campus research and library task' },
      { day: 'Thursday', focus: 'Project work and a museum visit' },
      { day: 'Friday', focus: 'Team presentations and feedback' },
    ],
    faqs: [
      { q: 'What level does my child need?', a: 'Students need an intermediate level. Your child takes a short online test before booking.' },
      { q: 'Which subjects can my child choose?', a: 'Subjects change each summer. Our team will send you the list for your dates.' },
    ],
    ctas: {
      heroPrimary: 'Request academic camp dates',
      heroSecondary: 'View a project week',
      midPrimary: 'See academic camp prices',
      midLink: { label: 'Compare with Language Camps', to: '/programmes/language-camps' },
      bottomPrimary: 'Send my camp question',
    },
    locations: ['london', 'oxfordshire', 'new-haven', 'los-angeles'],
  },
  {
    slug: 'mini-stays',
    name: 'Mini-stays',
    group: 'young',
    season: 'seasonal',
    keyword: 'english mini-stay for school groups',
    metaTitle: 'English Mini-stays for School Groups | Short Study Trips | ES World',
    metaDescription: 'Plan an English mini-stay for your school group. Lessons, trips and safe housing for one or two weeks. Ask for a group quote.',
    h1: 'English mini-stays for school groups',
    lead: 'An English mini-stay is a short study trip for school groups. Students take English lessons, visit places of interest and practise English every day for one or two weeks. Group leaders from your school travel with the group, and our team plans every detail with you.',
    cardText: 'Short study trips for school groups.',
    audience: 'School groups, ages 10 to 17',
    image: images.social,
    facts: [
      { label: 'Trip length', value: '1 to 2 weeks' },
      { label: 'Lessons', value: '000 a week' },
      { label: 'Ages', value: '10 to 17' },
      { label: 'Dates', value: 'Chosen by your school' },
    ],
    valueTitle: 'A short trip with real progress',
    valueText: 'Students use English in class in the morning and on visits in the afternoon. The programme fits your school calendar and your learning goals.',
    outcomes: [
      'Use English in real places every day',
      'Practise speaking with students from other countries',
      'Visit museums and places linked to your school topics',
      'Build confidence and independence',
      'Complete a group project in English',
      'Receive certificates for every student',
    ],
    whoTitle: 'Planned for schools and teachers',
    whoText: 'Mini-stays suit school groups of all sizes. We plan the dates, lessons and visits with the group leader.',
    whoFor: [
      { title: 'School teachers', text: 'You want a safe, planned trip for your class.' },
      { title: 'School leaders', text: 'You want a study trip that supports your curriculum.' },
      { title: 'Language departments', text: 'You want students to use English outside class.' },
      { title: 'Parents', text: 'You want a short, safe first trip abroad for your child.' },
    ],
    weekTitle: 'A sample mini-stay week',
    weekText: 'Mornings are for lessons. Afternoons are for visits linked to the lesson topics. Evenings have group activities.',
    week: [
      { day: 'Monday', focus: 'Arrival, welcome and level check' },
      { day: 'Tuesday', focus: 'Lessons, then a city walking tour' },
      { day: 'Wednesday', focus: 'Lessons, then a museum visit' },
      { day: 'Thursday', focus: 'Lessons, then project work' },
      { day: 'Friday', focus: 'Project presentations and certificates' },
    ],
    faqs: [
      { q: 'What is the minimum group size?', a: 'The minimum group size is 000 students. Ask our team about smaller groups.' },
      { q: 'Can you plan the trip around our topics?', a: 'Yes. Tell us your school topics, and we plan visits and lessons that match them.' },
    ],
    ctas: {
      heroPrimary: 'Ask for a group quote',
      heroSecondary: 'See a mini-stay week',
      midPrimary: 'See group prices',
      midLink: { label: 'Compare with Language Camps', to: '/programmes/language-camps' },
      bottomPrimary: 'Send my group enquiry',
    },
    locations: ['dubai', 'london', 'oxfordshire', 'tokyo', 'seoul', 'cape-town'],
  },
  {
    slug: 'high-school-programmes',
    name: 'High School Programmes',
    group: 'young',
    season: 'seasonal',
    keyword: 'high school abroad programme',
    metaTitle: 'High School Abroad Programme | Term or School Year | ES World',
    metaDescription: 'Join a high school abroad programme for a term or a full year. English support, school subjects and safe housing.',
    h1: 'High school abroad programmes for teens',
    lead: 'Our high school abroad programmes help teens study for a term or a full school year. Students improve their English, follow school subjects and live with a carefully chosen host family or in student housing. Our team supports students and parents from application to return.',
    cardText: 'Study abroad for a term or a full school year.',
    audience: 'Ages 14 to 18',
    image: images.young,
    facts: [
      { label: 'Length', value: '1 term to 1 school year' },
      { label: 'Timetable', value: 'Full school week' },
      { label: 'Ages', value: '14 to 18' },
      { label: 'Start dates', value: 'September or January' },
    ],
    valueTitle: 'Grow in a new school',
    valueText: 'Students take English support classes and regular school subjects. They learn to study, live and make friends in a new country.',
    outcomes: [
      'Reach a higher English level through daily use',
      'Follow school subjects taught in English',
      'Make friends with local and international students',
      'Build independence and life skills',
      'Receive a school report for each term',
      'Prepare for university study abroad in the future',
    ],
    whoTitle: 'For teens ready for a new school',
    whoText: 'These programmes suit teens with at least a pre-intermediate level who want to live and study abroad for a longer time.',
    whoFor: [
      { title: 'Students aged 14 to 18', text: 'You want to spend a term or a year at a school abroad.' },
      { title: 'Future applicants', text: 'You plan to apply to universities abroad.' },
      { title: 'Families', text: 'You want a safe, supported plan for your child.' },
      { title: 'Language learners', text: 'You want to use English every day, in and out of school.' },
    ],
    weekTitle: 'A sample school week',
    weekText: 'Students follow a normal school timetable with extra English support. Weekends are for activities and time with the host family or housing group.',
    week: [
      { day: 'Monday', focus: 'School subjects and an English support class' },
      { day: 'Tuesday', focus: 'School subjects and a sports club' },
      { day: 'Wednesday', focus: 'School subjects and study time' },
      { day: 'Thursday', focus: 'School subjects and a meeting with the student adviser' },
      { day: 'Friday', focus: 'School subjects and weekend planning' },
    ],
    faqs: [
      { q: 'Where does my child live?', a: 'Students live with a host family or in supervised student housing. Our team checks every option before placement.' },
      { q: 'How do parents stay informed?', a: 'Parents receive regular updates and school reports. You can contact our team at any time.' },
    ],
    ctas: {
      heroPrimary: 'Ask about school places',
      heroSecondary: 'See a school week',
      midPrimary: 'See programme fees',
      midLink: { label: 'Read our guide for parents', to: '/blog/english-summer-camp-guide-for-parents' },
      bottomPrimary: 'Send my school enquiry',
    },
    locations: ['london', 'oxfordshire', 'new-york', 'new-haven', 'los-angeles'],
  },
];
